// See services/sosInventoryService/README.md for information about the structure
// of schema.js files.
import { Decimal, Money } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import {
  getSalesTotal,
  getSalesSubTotal,
} from "services/sosInventoryService/salesTransaction/taxAndTotals";
import { formatDate } from "services/utility/dates";
import {
  extractName,
  extractLinkedTransactions,
  formatMoneyWithAdornments,
  formatCompanyInfo,
  formatDocumentInfo,
  formatNumberLinkField,
  formatStatus,
  formatSync,
  truncate,
  formatBooleanYesOrBlank,
  formatBooleanToYesOrBlank,
  extractPaymentId,
  extractDescription,
  formatQuantityWithUom,
  formatDecimal,
  formatMoney,
  formatLinkableItem,
  formatLinkableShipment,
  formatMoneyUnroundedWithAdornments,
} from "services/utility/formatting";
import { formatLineDecimal } from "services/utility/list";
import { parseHTML } from "services/utility/parsing";
import { TYPES } from "services/utility/schema";
import { starColumn } from "services/utility/schema";

import { NO_REF_NUMBER_STRING } from "appConstants";
import {
  DEFAULT_DECIMALS_UNROUNDED,
  DEFAULT_DECIMALS_ROUNDED,
} from "appConstants";

export const columnInfo = [
  starColumn,
  {
    name: "number",
    fixed: true,
    heading: i18n("columns.ReferenceNumber"),
    formatFunc: formatNumberLinkField,
    minWidth: "7em",
    linkField: true,
    sortable: true,
  },
  {
    name: "customer",
    heading: i18n("columns.Customer"),
    formatFunc: extractName,
    minWidth: "10em",
    maxWidth: "25em",
    default: true,
    sortable: true,
  },
  {
    name: "date",
    heading: i18n("columns.Date"),
    formatFunc: formatDate,
    defaultSort: true,
    sortDirection: "asc",
    sortable: true,
    default: true,
  },
  {
    name: "total",
    heading: i18n("columns.TotalAmount"),
    formatFunc: formatMoneyWithAdornments,
    align: "right",
    sortable: true,
    default: true,
  },
  {
    name: "comment",
    heading: i18n("columns.Comment"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "documents",
    formatFunc: formatDocumentInfo,
    heading: i18n("columns.Documents"),
    minWidth: "15em",
    default: true,
  },
  {
    name: "priority",
    heading: i18n("columns.Priority"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "shipping",
    heading: i18n("columns.Shipping"),
    formatFunc: formatCompanyInfo,
    minWidth: "10em",
    maxWidth: "15em",
  },
  {
    name: "canShip",
    heading: i18n("columns.CanShip"),
    sortable: true,
  },
  {
    name: "linkedPickTickets",
    heading: i18n("columns.PickTickets"),
    formatFunc: extractLinkedTransactions,
  },
  {
    name: "linkedShipments",
    heading: i18n("columns.Shipments"),
    formatFunc: extractLinkedTransactions,
  },
  {
    name: "location",
    heading: i18n("columns.Location"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "salesRep",
    heading: i18n("columns.SalesRep"),
    formatFunc: extractName,
    minWidth: "8em",
    sortable: true,
  },
  {
    name: "customerPO",
    heading: i18n("columns.CustomerPO"),
    sortable: true,
  },
  {
    name: "status",
    fieldName: "closed",
    heading: i18n("columns.Status"),
    formatFunc: formatStatus,
  },
  {
    name: "sync",
    fieldName: "syncMessage",
    heading: i18n("columns.Sync"),
    formatFunc: formatSync,
    sortable: true,
  },
  {
    name: "dueDate",
    fieldName: "earliestDueDate",
    heading: i18n("columns.DueDate"),
    formatFunc: formatDate,
    sortable: true,
  },
  {
    name: "orderStage",
    heading: i18n("columns.Stage"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "assignedToUser",
    heading: i18n("columns.AssignedTo"),
    formatFunc: extractName,
    minWidth: "10em",
    sortable: true,
  },
  {
    name: "channel",
    heading: i18n("columns.Channel"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "department",
    heading: i18n("columns.Department"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "customerNotes",
    heading: i18n("columns.CustomerNotes"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
  },
  {
    name: "billing",
    heading: i18n("columns.Billing"),
    formatFunc: formatCompanyInfo,
  },
  {
    name: "dropShip",
    heading: i18n("columns.DropShip"),
    formatFunc: formatBooleanYesOrBlank,
  },
  {
    name: "paymentMethod",
    heading: i18n("columns.PaymentMethod"),
    formatFunc: extractName,
  },
  {
    name: "depositAccount",
    heading: i18n("columns.DepositAccount"),
    formatFunc: extractName,
  },
  {
    name: "customerMessage",
    heading: i18n("columns.CustomerMessage"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
  },
  {
    name: "hasSignature",
    heading: i18n("columns.Signature"),
    formatFunc: formatBooleanToYesOrBlank,
  },
  {
    name: "fsPayment",
    heading: i18n("columns.Payments"),
    fieldName: "fsPaymentId",
    formatFunc: extractPaymentId,
    minWidth: "10em",
  },
];

export function getEmptyRecord(settings) {
  return {
    lines: [],
    starred: 0,
    syncToken: 1,
    number: "",
    date: new Date(),
    customer: null,
    location: null,
    depositAmount: new Money(0),
    subTotal: new Money(0),
    discountPercent: Decimal.ZERO,
    discountAmount: new Money(0),
    taxPercent: Decimal.ZERO,
    taxAmount: new Money(0),
    shippingAmount: new Money(0),
    total: new Money(0),
    billing: {
      company: null,
      contact: null,
      phone: null,
      email: null,
      addressName: null,
      addressType: null,
      address: {
        line1: null,
        line2: null,
        line3: null,
        line4: null,
        line5: null,
        city: null,
        stateProvince: null,
        postalCode: null,
        country: null,
      },
    },
    shipping: {
      company: null,
      contact: null,
      phone: null,
      email: null,
      addressName: null,
      addressType: null,
      address: {
        line1: null,
        line2: null,
        line3: null,
        line4: null,
        line5: null,
        city: null,
        stateProvince: null,
        postalCode: null,
        country: null,
      },
    },
    salesRep: null,
    channel: null,
    department: null,
    priority: null,
    assignedToUser: null,
    orderStage: null,
    taxCode: settings.defaultTaxCode,
    currency: settings.homeCurrencyRef,
    linkedTransaction: null,
    linkedShipments: [],
    transactionLocationQuickBooks: null,
    exchangeRate: new Decimal(1.0),
    customerMessage: null,
    statusMessage: null,
    comment: null,
    customerNotes: null,
    customFields: null,
    customerPO: null,
    canShip: null,
    statusLink: null,
    portalPassword: null,
    documents: [],
    keys: null,
    values: null,
    discountTaxable: settings.discountTaxable,
    shippingTaxable: false,
    dropShip: false,
    closed: false,
    archived: false,
    summaryOnly: false,
    hasSignature: false,
    paymentMethod: settings.defaultPaymentMethod,
    depositAccount: settings.defaultDepositAssetAccount,
    syncMessage: null,
    lastSync: "",
    trackingNumber: null,
    shipDate: null,
    shippingMethod: null,
    checkNumber: null,
    earliestDueDate: null,
  };
}

export const LINE_ITEM_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null, description: null },
  },
  class: { type: TYPES.reference },
  bin: { type: TYPES.reference },
  jobWorkcenter: { type: TYPES.reference },
  taxCode: { type: TYPES.reference },
  description: { type: TYPES.text },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  weight: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  weightunit: { type: TYPES.text },
  volume: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  volumeunit: { type: TYPES.text },
  unitprice: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  amount: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_ROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  altAmount: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  cost: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  uom: { type: TYPES.reference },
  serials: { type: TYPES.arrayOfReferences, initialValue: null },
  lot: { type: TYPES.reference },
  picked: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  shipped: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  invoiced: {
    type: TYPES.decimal,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  produced: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  returned: { type: TYPES.number, initialValue: Decimal.ZERO },
  listPrice: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  margin: {
    type: TYPES.decimal,
    currency: true,
    placesRight: DEFAULT_DECIMALS_ROUNDED,
    initialValue: Decimal.ZERO,
  },
  percentdiscount: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_ROUNDED,
    initialValue: Decimal.ZERO,
  },
  duedate: { type: TYPES.date, initialValue: () => new Date() },
  available: {
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  itemDetails: { initialValue: {} },
  relatedRecords: { initialValue: {} },
};

export const FIELDS_TO_TRANSFORM = {
  DECIMAL_FIELDS: ["exchangeRate", "discountPercent", "taxPercent"],
  MONEY_FIELDS: [
    "depositAmount",
    "discountAmount",
    "taxAmount",
    "total",
    "shippingAmount",
    "subTotal",
  ],
  DATE_FIELDS: ["date", "shipDate", "earliestDueDate"],
  DATETIME_FIELDS: ["lastSync"],
  NON_UTC_DATETIME_FIELDS: [],
};

export const REFERENCE_FIELDS = ["customer"];

export const LINE_ACTIONS = [
  ["quickView", "pdf", "email"],
  ["pickTicket", "shipment"],
  "salesOrder",
  "purchaseOrder",
  "transfer",
  "template",
  "RMA",
  ["job", "workOrder", "return"],
  "archive",
  "openClose",
  "delete",
  "history",
  "sync",
];
export const BATCH_ACTIONS = [
  ["pdf", "email"],
  "delete",
  "archive",
  "openClose",
  "merge",
  "sync",
];

export const ITEM_CALCULATED_FIELDS = ["available", "salesPrice"];

export const QUICK_VIEW_CONFIG = {
  footerSetting: "salesReceiptFooter",
  showStatusLink: true,
  isSalesTransaction: true,
  showLinkedShipment: true,
  addressConfig: [
    { name: "billing", label: i18n("address.BillTo") },
    { name: "shipping", label: i18n("address.ShipTo") },
  ],
  tableConfig: [{ type: "lines" }],
  headerFields: [
    {
      name: "date",
      formatFunc: formatDate,
      label: i18n("columns.Date"),
    },
    {
      name: "number",
      label: i18n("frmLabel.SaleNumber"),
      defaultValue: NO_REF_NUMBER_STRING,
    },
    {
      name: "location",
      formatFunc: extractName,
      label: i18n("frmLabel.Location"),
    },
    {
      name: "paymentMethod",
      formatFunc: extractName,
      label: i18n("frmLabel.PaymentMethod"),
    },
  ],
  fields: [
    {
      name: "linkedShipments",
      label: i18n("frmLabel.Shipments"),
      formatFunc: formatLinkableShipment,
    },
    {
      name: "customerPO",
      label: i18n("frmLabel.CustomerPO"),
    },
    {
      name: "customerMessage",
      label: i18n("frmLabel.CustomerMessage"),
    },
  ],
  lines: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
    },
    {
      name: "description",
      formatFunc: extractDescription,
      heading: i18n("columns.Description"),
      align: "left",
    },
    {
      name: "quantity",
      formatFunc: formatQuantityWithUom,
      heading: i18n("columns.Quantity"),
      align: "right",
    },
    {
      name: "picked",
      formatFunc: formatDecimal,
      heading: i18n("columns.Picked"),
      align: "right",
    },
    {
      name: "shipped",
      formatFunc: formatDecimal,
      heading: i18n("columns.Shipped"),
      align: "right",
    },
    {
      name: "unitprice",
      formatFunc: formatMoneyUnroundedWithAdornments,
      heading: i18n("columns.Rate"),
      align: "right",
    },
    {
      name: "amount",
      formatFunc: formatMoney,
      heading: i18n("columns.Amount"),
      align: "right",
      isTotalColumn: true,
    },
  ],
  tableTotals: [
    {
      label: i18n("quickView.SUBTOTAL"),
      formatFunc: getSalesSubTotal,
    },
    {
      label: i18n("quickView.DISCOUNT"),
      field: "discountAmount",
    },
    {
      label: i18n("quickView.SHIPPING"),
      field: "shippingAmount",
    },
    {
      label: i18n("quickView.TAX"),
      field: "taxAmount",
    },
    {
      label: i18n("quickView.TOTAL"),
      formatFunc: getSalesTotal,
      alwaysShow: true,
    },
    {
      label: i18n("quickView.PAYMENTS"),
      formatFunc: getSalesTotal,
      alwaysShow: true,
    },
    {
      label: i18n("quickView.BALANCEDUE"),
      formatFunc: ({ currency }) => Money.ZERO(currency?.name),
      alwaysShow: true,
    },
  ],
};
